/* eslint-disable sonarjs/no-nested-template-literals */
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CustomerProfileResponse,
  EzShipOrdersListResponse,
  OrdersListResponse,
  UplineNode,
} from '@/types/CustomerDetailModal';
import { ResponseError } from '@/types/Error';
import {
  AbandonedCartResponse,
  CancelledEzshipResponse,
  CustomerHistoryResponse,
  CustomerListResponse,
  DownlineEzShipListResponse,
  DownlineOrdersListResponse,
  DownlineReportResponse,
  DownLineTitleListResponse,
  DownLineTitleRanksListResponse,
  DownloadFileStatusReport,
  EzShipOrderInvoiceResponse,
  FastStartReportResponse,
  FormerPetProResponse,
  GroupVolumeReportResponse,
  LeadGenerationListResponse,
  MvrCapsApiResponse,
  OrderInvoiceItem,
  PendingEzshipOrdersListResponse,
  PeriodListResponse,
  PersonallyEnrolledListResponse,
  PetProfilesListResponse,
  PetProReportResponse,
  PopoverData,
  ProductSalesResponse,
  ProspectListReportResponse,
  QualificationHistoryResponse,
  RankUpWidgetResponse,
  TitleHistoryListResponse,
  TitlePromotionListResponse,
  TotalVolumeResponse,
  TreeViewerSearchResponse,
  TreeViewResponseDTO,
  VolumeBucketResponse,
} from '@/types/Reports';
import { SuccessResponse } from '@/types/Success';

import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';

const applicationJson = 'application/json';

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: 'reporting' }),
  tagTypes: ['GetCustomerList', 'CustomerProfile', 'OrdersList', 'GetTreeViewer'],
  endpoints: builder => ({
    getCustomerList: builder.query<
      SuccessResponse<CustomerListResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        url: `/GetCustomerList?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1&${
          searchKey ? `&searchKey=${searchKey}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<CustomerListResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getCustomerListDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        url: `/GetCustomerList?downloadexcelfile=true${searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getQualificationHistory: builder.query<
      SuccessResponse<QualificationHistoryResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        url: `/GetQualificationHistoryReport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1&${
          searchKey ? `&searchKey=${searchKey}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<QualificationHistoryResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getQualificationHistoryDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        url: `/GetQualificationHistoryReport?downloadexcelfile=true${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getPetPro: builder.query<
      SuccessResponse<PetProReportResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        url: `/GetPetProReport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1&${
          searchKey ? `&searchKey=${searchKey}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<PetProReportResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getPetProDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        url: `/GetPetProReport?downloadexcelfile=true${searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getFastStart: builder.query<
      SuccessResponse<FastStartReportResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        url: `/faststartreport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1&${
          searchKey ? `&searchKey=${searchKey}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<FastStartReportResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getFastStartDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        url: `/faststartreport?downloadexcelfile=true${searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getProspectList: builder.query<
      SuccessResponse<ProspectListReportResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        url: `/GetProsPectList?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1&${
          searchKey ? `&searchKey=${searchKey}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<ProspectListReportResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getProspectListDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        url: `/GetProsPectList?downloadexcelfile=true${searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getCustomerHistory: builder.query<
      SuccessResponse<CustomerHistoryResponse>,
      {
        start: number;
        limit: number;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        searchKey?: string;
        startDate?: string;
        endDate?: string;
      }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey, startDate, endDate }) => ({
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        url: `/GetCustomerHistory?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
          /* eslint-disable-next-line sonarjs/no-nested-template-literals*/
        }${startDate ? `&startdate=${startDate}` : ''}${endDate ? `&enddate=${endDate}` : ''}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<CustomerHistoryResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getCustomerHistoryDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        isExportAll: boolean;
        searchKey?: string;
        startDate?: string;
        endDate?: string;
      }
    >({
      query: ({ startDate, endDate, searchKey, isExportAll }) => ({
        url: `/GetCustomerHistory?downloadexcelfile=true&limit=-1&isExportAll=${isExportAll || false}${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
          /* eslint-disable-next-line sonarjs/no-nested-template-literals*/
        }${startDate ? `&startdate=${startDate}` : ''}${endDate ? `&enddate=${endDate}` : ''}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getCustomerProfile: builder.query<SuccessResponse<CustomerProfileResponse>, { token: string }>({
      query: ({ token }) => ({
        url: `/GetProfile?token=${token}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<CustomerProfileResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getUplineNodes: builder.query<UplineNode[], { sort: { field: string; dir: 'asc' | 'desc' }[]; customerId: number }>(
      {
        query: ({ sort, customerId }) => ({
          url: `/GetUplineNodes?customerId=${customerId}`,
          method: 'POST',
          headers: { 'Content-Type': applicationJson },
          body: sort,
        }),
        transformResponse: (response: UplineNode[]) => response,
        transformErrorResponse: response => response as ResponseError,
      }
    ),

    getCustomerOrders: builder.query<
      SuccessResponse<OrdersListResponse>,
      { customerId: number; take: number; skip: number; page: number; pageSize: number; sort?: any; filter?: any }
    >({
      query: ({ customerId, take, skip, page, pageSize, sort, filter }) => ({
        url: `/OrdersList?customerId=${customerId}`,
        method: 'POST',
        headers: { 'Content-Type': applicationJson },
        body: {
          take,
          skip,
          page,
          pageSize,
          sort: sort || [],
          filter: filter || { logic: 'and', filters: [] },
        },
      }),
      transformResponse: (response: SuccessResponse<OrdersListResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getEzShipOrders: builder.query<
      SuccessResponse<EzShipOrdersListResponse>,
      { customerId: number; take: number; skip: number; page: number; pageSize: number; sort: any; filter: any }
    >({
      query: ({ customerId, take, skip, page, pageSize, sort, filter }) => ({
        url: `/EzShipOrdersList?customerId=${customerId}`,
        method: 'POST',
        body: {
          take,
          skip,
          page,
          pageSize,
          sort,
          filter: filter || { logic: 'and', filters: [] },
        },
      }),
    }),

    getProductSalesList: builder.query<
      SuccessResponse<ProductSalesResponse>,
      {
        start: number;
        limit: number;
        isExportAll: boolean;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        draw: number;
        searchKey?: string;
        startDate?: string;
        endDate?: string;
      }
    >({
      query: ({ start, limit, isExportAll, orderColumn, orderDir, draw, searchKey, startDate, endDate }) => ({
        url: `/GetProductSalesListByPeriodId?start=${start}&limit=${limit}&isExportAll=${
          isExportAll || false
        }&ordercolumn=${orderColumn}&orderdir=${orderDir}&draw=${draw}&downloadexcelfile=false${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
          /* eslint-disable-next-line sonarjs/no-nested-template-literals*/
        }${startDate ? `&startdate=${startDate}` : ''}${endDate ? `&enddate=${endDate}` : ''}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<ProductSalesResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getReportListDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        isExportAll: boolean;
        startDate?: string;
        endDate?: string;
        searchKey?: string;
      }
    >({
      query: ({ isExportAll, startDate, endDate, searchKey }) => ({
        url: `/GetProductSalesListByPeriodId?downloadexcelfile=true&isExportAll=${isExportAll || false}${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
          /* eslint-disable-next-line sonarjs/no-nested-template-literals*/
        }${startDate ? `&startdate=${startDate}` : ''}${endDate ? `&enddate=${endDate}` : ''}&limit=-1`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getMvrCapsList: builder.query<
      SuccessResponse<MvrCapsApiResponse>,
      {
        start: number;
        limit: number;
        isExportAll: boolean;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        draw: number;
        periodid: number;
        searchKey?: string;
      }
    >({
      query: ({ start, limit, isExportAll, orderColumn, orderDir, draw, periodid, searchKey }) => ({
        url: `/getmvrcapsreport?start=${start}&limit=${limit}&isExportAll=${
          isExportAll || false
        }&ordercolumn=${orderColumn}&orderdir=${orderDir}&draw=${draw}&periodid=${periodid}&downloadexcelfile=false${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<MvrCapsApiResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getMvrCapsDownloadUrl: builder.query<
      SuccessResponse<String>,
      { isExportAll: boolean; periodid: number; searchKey?: string }
    >({
      query: ({ isExportAll, periodid, searchKey }) => ({
        url: `/getmvrcapsreport?downloadexcelfile=true&periodid=${periodid}&isExportAll=${isExportAll || false}${
          searchKey ? `&searchKey=${searchKey}` : ''
        }&limit=-1`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getProductSalesReportPeriods: builder.query<SuccessResponse<PeriodListResponse>, void>({
      query: () => ({
        url: '/GetCustomerPeriodList',
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<PeriodListResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getOrderInvoiceDetails: builder.query<SuccessResponse<OrderInvoiceItem>, { token: string; customerId: number }>({
      query: ({ token, customerId }) => ({
        url: `/OrderInvoice?token=${token}&customerId=${customerId}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<OrderInvoiceItem>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getAutoOrderInvoiceDetails: builder.query<
      SuccessResponse<EzShipOrderInvoiceResponse>,
      { autoorderid: string; customerid: number }
    >({
      query: ({ autoorderid, customerid }) => ({
        url: `/GetAutoOrderInvoice?autoorderid=${autoorderid}&customerid=${customerid}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<EzShipOrderInvoiceResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getLeadGenerationList: builder.query<
      SuccessResponse<LeadGenerationListResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        url: `/GetLeadGenerationList?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadExcelFile=false&draw=1&${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),

      transformResponse: (response: SuccessResponse<LeadGenerationListResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getLeadGenerationListDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        url: `/GetLeadGenerationList?downloadExcelFile=true&limit=-1${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<string>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getAbandonedCartReport: builder.query<
      SuccessResponse<AbandonedCartResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        url: `/GetAbandonedCartReport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<AbandonedCartResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getAbandonedCartReportListDownloadUrl: builder.query<
      SuccessResponse<String>,
      { searchKey?: string; isExportAll?: boolean }
    >({
      query: ({ searchKey, isExportAll }) => ({
        url: isExportAll
          ? `/GetAbandonedCartReport?downloadExcelFile=true&limit=-1&exportAll=${isExportAll || false}`
          : `/GetAbandonedCartReport?downloadExcelFile=true&limit=-1${
              searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
            }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getRankUpWidget: builder.query<
      SuccessResponse<RankUpWidgetResponse>,
      {
        faststartenddate?: `${number}/${number}/${number}` | string;
        date2?: `${number}/${number}/${number}` | string;
        careerrankid?: number;
        custid: number;
        rankid?: number | string;
      }
    >({
      query: params => {
        const queryString = Object.entries(params)
          .filter(([_, value]) => value !== undefined)
          .map(([key, value]) => `${key}=${value}`)
          .join('&');
        return {
          url: `GetRankUpWidget?${queryString}`,
          method: 'GET',
          headers: { 'Content-Type': applicationJson },
        };
      },
      transformResponse: (response: SuccessResponse<RankUpWidgetResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getPetProfilesList: builder.query<
      SuccessResponse<PetProfilesListResponse>,
      {
        start: number;
        limit: number;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        searchKey?: string;
        startDate: string;
        endDate: string;
      }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey, startDate, endDate }) => ({
        url: `/PetProfileReportData?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadExcelFile=false&draw=1${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
          /* eslint-disable-next-line sonarjs/no-nested-template-literals*/
        }${startDate ? `&startdate=${startDate}` : ''}${endDate ? `&enddate=${endDate}` : ''}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<PetProfilesListResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getPetProfilesListDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        isExportAll: boolean;
        searchKey?: string;
        startDate: string;
        endDate: string;
      }
    >({
      query: ({ startDate, endDate, searchKey, isExportAll }) => ({
        url: `/PetProfileReportData?downloadExcelFile=true&limit=-1&exportAll=${isExportAll || false}${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
          /* eslint-disable-next-line sonarjs/no-nested-template-literals*/
        }${startDate ? `&startdate=${startDate}` : ''}${endDate ? `&enddate=${endDate}` : ''}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getVolumeBucketDetails: builder.query<
      SuccessResponse<VolumeBucketResponse>,
      { customerid: number; volumebucket: number; selectedPeriodid: number | undefined }
    >({
      query: ({ customerid, volumebucket, selectedPeriodid }) => ({
        url: `/ModalDetails?customerid=${customerid}&volumebucket=${volumebucket}${
          selectedPeriodid ? `&selectedPeriodid=${selectedPeriodid}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<VolumeBucketResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getCancelledEzship: builder.query<
      SuccessResponse<CancelledEzshipResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        url: `/CancelledEzshipReport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1&${
          searchKey ? `&searchKey=${searchKey}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<CancelledEzshipResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getCancelledEzshipDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        url: `/CancelledEzshipReport?downloadexcelfile=true${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getFileDownloadStatus: builder.query<SuccessResponse<DownloadFileStatusReport>, { fileId: string }>({
      query: ({ fileId }) => ({
        url: `/GetFileDownloadStatus?fileId=${fileId}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<DownloadFileStatusReport>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    cancelExcelReport: builder.mutation<SuccessResponse<{ message: string }>, { fileId: string }>({
      query: ({ fileId }) => ({
        url: `/CancelExcelReport?fileId=${fileId}`,
        method: 'DELETE',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<{ message: string }>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getExcelReportDownload: builder.query<Blob, { filePath: string }>({
      query: ({ filePath }) => ({
        url: `/DownloadExcelReport?filePath=${encodeURIComponent(filePath)}`,
        method: 'GET',
        responseHandler: async response => {
          if (!response.ok) throw new Error('Failed to fetch the file');
          return response.blob();
        },
        headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      }),
    }),
    getTitlePromotionList: builder.query<
      SuccessResponse<TitlePromotionListResponse>,
      {
        start: number;
        limit: number;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        draw: number;
        searchKey?: string;
      }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey, draw }) => ({
        url: `/GetTitlePromotionReport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadExcelFile=false&draw=${draw}${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<TitlePromotionListResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getDownlineOrdersList: builder.query<
      SuccessResponse<DownlineOrdersListResponse>,
      {
        start: number;
        limit: number;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        draw: number;
        searchKey?: string;
        orderTypeFilter: string | null;
      }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey, draw, orderTypeFilter }) => ({
        url: `/DownlineOrders?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadExcelFile=false&draw=${draw}${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }${orderTypeFilter ? `&orderTypeFilter=${encodeURIComponent(orderTypeFilter)}` : ''}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<DownlineOrdersListResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getDownlineEzshipList: builder.query<
      SuccessResponse<DownlineEzShipListResponse>,
      {
        start: number;
        limit: number;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        draw: number;
        searchKey?: string;
      }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey, draw }) => ({
        url: `/DownlineAutoOrders?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadExcelFile=false&draw=${draw}${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<DownlineEzShipListResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getTitlePromotionListDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        url: `/GetTitlePromotionReport?downloadExcelFile=true${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getDownlineTitleList: builder.query<
      SuccessResponse<DownLineTitleListResponse>,
      {
        start: number;
        limit: number;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        draw: number;
        rankId: number;
        searchKey?: string;
      }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey, draw, rankId }) => ({
        url: `/GetDownlineTitleReport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadExcelFile=false&rankid=${rankId}&draw=${draw}${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<DownLineTitleListResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getDownlineOrdersDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
        orderTypeFilter?: string | null;
      }
    >({
      query: ({ searchKey, orderTypeFilter }) => ({
        url: `/DownlineOrders?downloadExcelFile=true${searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''}${
          orderTypeFilter ? `&orderTypeFilter=${encodeURIComponent(orderTypeFilter)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getDownlineEzshipDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        url: `/DownlineAutoOrders?downloadExcelFile=true${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    // Volume Reports
    getGroupVolumeReportList: builder.query<
      SuccessResponse<GroupVolumeReportResponse>,
      {
        start: number;
        limit: number;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        draw: number;
        periodid: number;
        searchKey?: string;
      }
    >({
      query: ({ start, limit, orderColumn, orderDir, draw, periodid, searchKey }) => ({
        url: `/getgroupvolumereport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&draw=${draw}&periodid=${periodid}&downloadexcelfile=false${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<GroupVolumeReportResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getGroupVolumeReportDownloadUrl: builder.query<
      SuccessResponse<String>,
      { isExportAll: boolean; periodid: number; searchKey?: string }
    >({
      query: ({ isExportAll, periodid, searchKey }) => ({
        url: `/getgroupvolumereport?downloadexcelfile=true&periodid=${periodid}&isExportAll=${isExportAll || false}${
          searchKey ? `&searchKey=${searchKey}` : ''
        }&limit=-1`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getDownlineReportList: builder.query<
      SuccessResponse<DownlineReportResponse>,
      {
        start: number;
        limit: number;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        draw: number;
        periodid: number;
        searchKey?: string;
      }
    >({
      query: ({ start, limit, orderColumn, orderDir, draw, periodid, searchKey }) => ({
        url: `/downlinereport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&draw=${draw}&periodid=${periodid}&downloadexcelfile=false${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<DownlineReportResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getTotalVolumes: builder.query<
      SuccessResponse<TotalVolumeResponse>,
      {
        custid: number;
      }
    >({
      query: ({ custid }) => ({
        url: `/GetVolumesDetails?custid=${custid}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<TotalVolumeResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getDownlineReportDownloadUrl: builder.query<
      SuccessResponse<String>,
      { isExportAll: boolean; periodid: number; searchKey?: string }
    >({
      query: ({ isExportAll, periodid, searchKey }) => ({
        url: `/downlinereport?downloadexcelfile=true&periodid=${periodid}&isExportAll=${isExportAll || false}${
          searchKey ? `&searchKey=${searchKey}` : ''
        }&limit=-1`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getPersonallyEnrolledTeamList: builder.query<
      SuccessResponse<PersonallyEnrolledListResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        url: `/GetPersonallyEnrolledList?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1&${
          searchKey ? `&searchKey=${searchKey}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<PersonallyEnrolledListResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getPersonallyEnrolledTeamDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        url: `/GetPersonallyEnrolledList?downloadexcelfile=true${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    // END Volume Reports

    getDownlineTitleListDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        rankId: number;
        searchKey?: string;
      }
    >({
      query: ({ searchKey, rankId }) => ({
        url: `/GetDownlineTitleReport?downloadExcelFile=true&rankid=${rankId}${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getTitleHistoryList: builder.query<
      SuccessResponse<TitleHistoryListResponse>,
      {
        start: number;
        limit: number;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        draw: number;
        searchKey?: string;
      }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey, draw }) => ({
        url: `/GetTitleHistoryReport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadExcelFile=false&draw=${draw}${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),

      transformResponse: (response: SuccessResponse<TitleHistoryListResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getTitleHistoryListDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        url: `/GetTitleHistoryReport?downloadExcelFile=true${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getDownlineTitleRanskList: builder.query<
      SuccessResponse<DownLineTitleRanksListResponse>,
      {
        start: number;
        limit: number;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        draw: number;
        searchKey?: string;
      }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey, draw }) => ({
        url: `/GetDownlineTitle?rankId=0`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),

      transformResponse: (response: SuccessResponse<DownLineTitleRanksListResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getFormerPetProList: builder.query<
      SuccessResponse<FormerPetProResponse>,
      {
        start: number;
        limit: number;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        draw: number;
        searchKey?: string;
      }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey, draw }) => ({
        url: `/FormerPetProReport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadExcelFile=false&draw=${draw}${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),

      transformResponse: (response: SuccessResponse<FormerPetProResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getFormerPetProListDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        url: `/FormerPetProReport?downloadExcelFile=true${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getPendingEzshipOrdersReportDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        url: `/pendingezshiporders?downloadExcelFile=true${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getTreeViewer: builder.query<
      SuccessResponse<TreeViewResponseDTO>,
      {
        careerrankid?: number;
        customerId: number;
        rankid?: number | string;
      }
    >({
      query: params => {
        const queryString = Object.entries(params)
          .filter(([_, value]) => value !== undefined)
          .map(([key, value]) => `${key}=${value}`)
          .join('&');
        return {
          url: `FetchUnilevelTree?${queryString}`,
          method: 'GET',
          headers: { 'Content-Type': applicationJson },
        };
      },
      transformResponse: (response: SuccessResponse<TreeViewResponseDTO>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getSearchedTreeViewerResults: builder.query<
      SuccessResponse<TreeViewerSearchResponse>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        url: `/UniLevelSearch${searchKey ? `?query=${encodeURIComponent(searchKey)}` : ''}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<TreeViewerSearchResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    getNodePopoverData: builder.query<
      SuccessResponse<PopoverData>,
      {
        id: number;
      }
    >({
      query: ({ id }) => ({
        url: `/popoversummary?id=${id}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<PopoverData>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getPendingEzShipOrdersList: builder.query<
      SuccessResponse<PendingEzshipOrdersListResponse>,
      {
        start: number;
        limit: number;
        orderColumn: number;
        orderDir: 'asc' | 'desc';
        searchKey?: string;
        startDate?: string;
        endDate?: string;
        periodid?: number;
      }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey, startDate, endDate, periodid }) => ({
        url: `/PendingEZShipReport?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }${periodid ? `&periodid=${periodid}` : ''}`,
        // url: `/getPendingEzShipOrdersList?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1&${
        //   searchKey ? `&searchKey=${searchKey}` : ''
        // }${startDate ? `&startDate=${encodeURIComponent(startDate)}` : ''}${
        //   endDate ? `&endDate=${encodeURIComponent(endDate)}` : ''
        // }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<PendingEzshipOrdersListResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
  }),
});

export const {
  useGetPetProQuery,
  useLazyGetPetProDownloadUrlQuery,
  useGetCustomerListQuery,
  useLazyGetCustomerListDownloadUrlQuery,
  useGetQualificationHistoryQuery,
  useLazyGetQualificationHistoryDownloadUrlQuery,
  useGetFastStartQuery,
  useLazyGetFastStartDownloadUrlQuery,
  useGetCustomerHistoryQuery,
  useLazyGetCustomerHistoryDownloadUrlQuery,
  useLazyGetCustomerProfileQuery,
  useLazyGetCustomerOrdersQuery,
  useLazyGetEzShipOrdersQuery,
  useGetProductSalesListQuery,
  useLazyGetReportListDownloadUrlQuery,
  useGetProductSalesReportPeriodsQuery,
  useLazyGetOrderInvoiceDetailsQuery,
  useLazyGetAutoOrderInvoiceDetailsQuery,
  useGetLeadGenerationListQuery,
  useLazyGetLeadGenerationListDownloadUrlQuery,
  useLazyGetUplineNodesQuery,
  useGetAbandonedCartReportQuery,
  useLazyGetAbandonedCartReportListDownloadUrlQuery,
  useLazyGetRankUpWidgetQuery,
  useGetPetProfilesListQuery,
  useLazyGetPetProfilesListDownloadUrlQuery,
  useGetProspectListQuery,
  useLazyGetProspectListDownloadUrlQuery,
  useGetMvrCapsListQuery,
  useLazyGetMvrCapsDownloadUrlQuery,
  useLazyGetVolumeBucketDetailsQuery,
  useGetCancelledEzshipQuery,
  useLazyGetCancelledEzshipDownloadUrlQuery,
  useLazyGetFileDownloadStatusQuery,
  useCancelExcelReportMutation,
  useLazyGetExcelReportDownloadQuery,
  useGetTitleHistoryListQuery,
  useLazyGetTitleHistoryListDownloadUrlQuery,
  useGetTitlePromotionListQuery,
  useLazyGetTitlePromotionListDownloadUrlQuery,
  useGetDownlineTitleListQuery,
  useLazyGetDownlineTitleListDownloadUrlQuery,
  useGetDownlineTitleRanskListQuery,
  useGetFormerPetProListQuery,
  useLazyGetFormerPetProListDownloadUrlQuery,
  useGetDownlineOrdersListQuery,
  useGetDownlineEzshipListQuery,
  useGetGroupVolumeReportListQuery,
  useGetPersonallyEnrolledTeamListQuery,
  useLazyGetPersonallyEnrolledTeamDownloadUrlQuery,
  useLazyGetGroupVolumeReportDownloadUrlQuery,
  useLazyGetDownlineOrdersDownloadUrlQuery,
  useLazyGetDownlineEzshipDownloadUrlQuery,
  useLazyGetTreeViewerQuery,
  useGetDownlineReportListQuery,
  useLazyGetTotalVolumesQuery,
  useLazyGetDownlineReportDownloadUrlQuery,
  useLazyGetSearchedTreeViewerResultsQuery,
  useLazyGetNodePopoverDataQuery,
  useGetPendingEzShipOrdersListQuery,
  useLazyGetPendingEzshipOrdersReportDownloadUrlQuery,
} = reportsApi;
